import { odooApi } from '../index';
import { Event, PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { extractErrorMessage, transformOdooPaginatedResponse } from '../transforms';
import { buildImageUrl } from '@/config/api.config';
import DOMPurify from 'dompurify';

/**
 * Events API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains all event-related API endpoints.
 * Connected to real Odoo API at /api/moys/events
 */

/**
 * Odoo Event API Response Type
 */
interface OdooEvent {
  id: number;
  name: string; // Translated based on lang parameter
  description: string; // HTML content, translated
  event_date: string; // ISO date string
  location: string; // Translated
  image: string | null; // Secure URL or base64
  categories: Array<{
    id: number;
    name: string; // Translated
    color: string;
  }>;
  url?: string | null; // Registration URL
  add_from_google_form?: boolean;
  google_form_url?: string | null;
  status?: string;
}

/**
 * Transform Odoo event to frontend Event type
 */
function transformOdooEventToEvent(
  odooEvent: OdooEvent,
  lang: 'ar' | 'en' | 'ku'
): Event {
  // Map first category or default to 'other'
  const category = odooEvent.categories && odooEvent.categories.length > 0
    ? mapCategoryName(odooEvent.categories[0].name)
    : 'other';

  // Extract description HTML and sanitize
  const descriptionHtml = odooEvent.description || '';
  const sanitizedDescription = DOMPurify.sanitize(descriptionHtml);

  return {
    id: odooEvent.id.toString(),
    titleAr: lang === 'ar' ? odooEvent.name : odooEvent.name,
    titleEn: lang === 'en' ? odooEvent.name : odooEvent.name,
    titleKu: lang === 'ku' ? odooEvent.name : odooEvent.name,
    start: odooEvent.event_date || new Date().toISOString(),
    end: odooEvent.event_date || new Date().toISOString(), // Events API doesn't return end_date, use start_date
    location: odooEvent.location || '',
    description: {
      ar: sanitizedDescription,
      en: sanitizedDescription,
      ku: sanitizedDescription,
    },
    category: category as Event['category'],
    registrationRequired: !!(odooEvent.url && odooEvent.url.trim()), // Infer from url field
    registrationUrl: (odooEvent.url && odooEvent.url.trim()) ? odooEvent.url : undefined,
    image: odooEvent.image ? (buildImageUrl(odooEvent.image) || odooEvent.image) : undefined,
    organizerId: '1', // Not provided by API
    createdAt: new Date().toISOString(),
  };
}

/**
 * Map category name from API to frontend category type
 */
function mapCategoryName(categoryName: string): Event['category'] {
  const normalized = categoryName.toLowerCase().trim();
  if (normalized.includes('festival') || normalized.includes('مهرجان')) return 'festival';
  if (normalized.includes('seminar') || normalized.includes('ندوة')) return 'seminar';
  if (normalized.includes('conference') || normalized.includes('مؤتمر')) return 'conference';
  if (normalized.includes('sport') || normalized.includes('رياضي')) return 'sport';
  if (normalized.includes('training') || normalized.includes('تدريب')) return 'training';
  return 'other';
}

export const eventsApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all events with pagination
    getEvents: builder.query<
      PaginatedResponse<Event>,
      { page?: number; pageSize?: number; lang?: 'ar' | 'en' | 'ku' }
    >({
      query: ({ page = 1, pageSize = 100, lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);
        
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });

        return {
          url: `/api/moys/events?${params.toString()}`,
          method: 'POST',
          // No body - backend reads from query parameters
        };
      },
      transformResponse: (response: any, meta, arg) => {
        try {
          if (!response.success || !response.data) {
            throw new Error('Invalid response format');
          }

          return transformOdooPaginatedResponse(
            response,
            transformOdooEventToEvent,
            arg.page || 1,
            arg.pageSize || 100,
            arg.lang || 'ar'
          );
        } catch (error) {
          console.error('[Events API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result) => {
        if (!result) {
          return [{ type: 'Event', id: 'LIST' }];
        }
        
        if (result.data && Array.isArray(result.data)) {
          return [
            ...result.data.map((event) => ({ type: 'Event' as const, id: event.id })),
            { type: 'Event', id: 'LIST' },
          ];
        }
        
        return [{ type: 'Event', id: 'LIST' }];
      },
    }),

    // Get single event by ID
    getEvent: builder.query<Event, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      async queryFn({ id, lang = 'ar' }, api, extraOptions, baseQuery) {
        const odooLang = getOdooLang(lang);
        const result = await baseQuery({
          url: `/api/moys/events/${id}?lang=${odooLang}`,
          method: 'POST',
        });
        
        if (result.error) {
          return { error: result.error as any };
        }
        
        try {
          const response = result.data as any;
          
          if (!response.success || !response.data) {
            return { error: { status: 404, data: 'Event not found' } as any };
          }
          
          const event = transformOdooEventToEvent(response.data, lang);
          return { data: event };
        } catch (error) {
          console.error('[Events API] Get single event error:', error);
          return { error: { status: 500, data: extractErrorMessage(error) } as any };
        }
      },
      providesTags: (result, error, { id }) => [{ type: 'Event', id }],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetEventQuery,
} = eventsApi;

